import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

// Camera-helper trim: how many points to process between async yields when precomputing the
// per-vertex "nearest helper" distance. Keeps the UI responsive on large clouds.
const TRIM_CHUNK = 50000;

// Per-scan camera-path colours, assigned by the scan's index in the version so the same scan gets
// the same colour in both the alignment editor and the runtime viewer. Cycles if there are more
// scans than colours.
export const CAMERA_PATH_COLORS = ['#64ffda', '#ffb454', '#7aa2ff', '#ff7eb6', '#c792ea', '#9ccc65', '#ff6e6e', '#4dd0e1'];

/**
 * Camera-helper-radius point-cloud culling, shared by the alignment editor (ViewerEngine) and the
 * runtime viewer (ModelViewer). Each scan is rendered with this material; once `attachCullDistances`
 * has stored a per-vertex distance-to-nearest-helper attribute, `setMaterialTrimRadius` is an O(1)
 * uniform swap and the GPU hides points outside the tunnel in real time.
 */

/**
 * Wires the cull shader hook onto a points material. Safe to call on any THREE.PointsMaterial; the
 * shader is a no-op until both an `aMinDistance` BufferAttribute and a uTrimRadius > 0 are present.
 *
 * The current radius lives on `material.userData.trimRadius` so it survives the recompiles that
 * happen when the caller toggles `transparent` / changes `size` — onBeforeCompile re-reads it each
 * compile, and `setMaterialTrimRadius` patches both userData and the live uniform.
 */
export function installCullShader(material) {
  if (material.userData.cullShaderInstalled) return material;

  material.userData.trimRadius = material.userData.trimRadius ?? 0;
  // Decimation: keep only points whose stable per-vertex key (aDecimateKey, 0..1) is <= uDecimateKeep.
  // 1 = keep everything (off). Uniform-random thinning for previewing a decimated/baked density live.
  material.userData.decimateKeep = material.userData.decimateKeep ?? 1;
  // Zone-isolation clip box: when enabled, points outside an oriented box are culled. uClipMatrix
  // is world→box-local (the box spans [-0.5, 0.5] in that space, i.e. inverse of the zone's world
  // matrix on a unit BoxGeometry). Off by default; toggled live via setMaterialClipBox.
  material.userData.clipEnabled = material.userData.clipEnabled ?? 0;
  material.userData.clipMatrix = material.userData.clipMatrix ?? new THREE.Matrix4();
  // Axis-aligned world-space slice box (the "cut-away" tool): points whose WORLD position falls
  // outside [sliceMin, sliceMax] on any axis are culled. Independent of the zone clip box above, so
  // a slice and a zone isolation can be active at once. Off by default; toggled via setMaterialSliceBox.
  material.userData.sliceEnabled = material.userData.sliceEnabled ?? 0;
  material.userData.sliceMin = material.userData.sliceMin ?? new THREE.Vector3(-1e9, -1e9, -1e9);
  material.userData.sliceMax = material.userData.sliceMax ?? new THREE.Vector3(1e9, 1e9, 1e9);
  material.userData.cullShaderInstalled = true;

  material.onBeforeCompile = (shader) => {
    shader.uniforms.uTrimRadius = { value: material.userData.trimRadius };
    shader.uniforms.uDecimateKeep = { value: material.userData.decimateKeep };
    shader.uniforms.uClipEnabled = { value: material.userData.clipEnabled };
    shader.uniforms.uClipMatrix = { value: material.userData.clipMatrix };
    shader.uniforms.uSliceEnabled = { value: material.userData.sliceEnabled };
    shader.uniforms.uSliceMin = { value: material.userData.sliceMin };
    shader.uniforms.uSliceMax = { value: material.userData.sliceMax };
    shader.vertexShader =
      'attribute float aMinDistance;\nattribute float aDecimateKey;\nuniform float uTrimRadius;\nuniform float uDecimateKeep;\nuniform float uClipEnabled;\nuniform mat4 uClipMatrix;\nuniform float uSliceEnabled;\nuniform vec3 uSliceMin;\nuniform vec3 uSliceMax;\n'
      + shader.vertexShader;

    // Cull culled points two ways for cross-GPU robustness: gl_PointSize = 0 (most GPUs skip
    // rasterization), and gl_Position pushed outside the clip volume so anything that does try
    // to rasterize gets clipped. Only the size trick leaves a single dot on some drivers because
    // gl_PointSize < 1 still emits a fragment at the vertex's pixel.
    shader.vertexShader = shader.vertexShader.replace(
      '#include <fog_vertex>',
      [
        '#include <fog_vertex>',
        'if (uTrimRadius > 0.0 && aMinDistance > uTrimRadius) {',
        '  gl_PointSize = 0.0;',
        '  gl_Position = vec4(2.0, 2.0, 2.0, 1.0);',
        '}',
        // Decimation: drop points whose stable key exceeds the keep threshold (uniform random).
        'if (uDecimateKeep < 1.0 && aDecimateKey > uDecimateKeep) {',
        '  gl_PointSize = 0.0;',
        '  gl_Position = vec4(2.0, 2.0, 2.0, 1.0);',
        '}',
        // Isolate-to-zone: discard points outside the oriented clip box. The point's world
        // position (modelMatrix * position) is taken into box-local space; outside [-0.5,0.5]
        // on any axis = outside the zone.
        'if (uClipEnabled > 0.5) {',
        '  vec3 zoneLocal = (uClipMatrix * modelMatrix * vec4(position, 1.0)).xyz;',
        '  if (abs(zoneLocal.x) > 0.5 || abs(zoneLocal.y) > 0.5 || abs(zoneLocal.z) > 0.5) {',
        '    gl_PointSize = 0.0;',
        '    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);',
        '  }',
        '}',
        // Slice / cut-away: discard points whose WORLD position is outside the axis-aligned slice box.
        'if (uSliceEnabled > 0.5) {',
        '  vec3 sliceWorld = (modelMatrix * vec4(position, 1.0)).xyz;',
        '  if (sliceWorld.x < uSliceMin.x || sliceWorld.x > uSliceMax.x || sliceWorld.y < uSliceMin.y || sliceWorld.y > uSliceMax.y || sliceWorld.z < uSliceMin.z || sliceWorld.z > uSliceMax.z) {',
        '    gl_PointSize = 0.0;',
        '    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);',
        '  }',
        '}',
      ].join('\n  '),
    );

    material.userData.shader = shader;
  };

  return material;
}

/**
 * Updates the live trim radius on a culling material. No-op for materials that haven't had
 * `installCullShader` called on them.
 */
export function setMaterialTrimRadius(material, radius) {
  if (!material) return;
  const value = Number(radius) || 0;
  material.userData.trimRadius = value;

  const shader = material.userData.shader;
  if (shader?.uniforms?.uTrimRadius) {
    shader.uniforms.uTrimRadius.value = value;
  } else if (material.userData.cullShaderInstalled) {
    // No compile has happened yet — flag a (re)compile so onBeforeCompile picks up the value.
    material.needsUpdate = true;
  }
}

/**
 * Sets the live decimation keep-fraction (0..1) on a culling material — the fraction of points to
 * KEEP (1 = all, 0.6 = thin to 60%). Uniform-random via the per-vertex aDecimateKey. No-op for
 * materials that haven't had `installCullShader` called.
 */
export function setMaterialDecimation(material, keepFraction) {
  if (!material) return;
  const value = Math.min(1, Math.max(0, Number(keepFraction) ?? 1));
  material.userData.decimateKeep = value;

  const shader = material.userData.shader;
  if (shader?.uniforms?.uDecimateKeep) {
    shader.uniforms.uDecimateKeep.value = value;
  } else if (material.userData.cullShaderInstalled) {
    material.needsUpdate = true;
  }
}

/**
 * Assigns every vertex a stable pseudo-random key in [0,1) as the `aDecimateKey` attribute, so the
 * decimation shader can keep a uniform-random subset by thresholding. Computed once at load; the
 * randomness only needs to be spatially uniform within this session (the bake recomputes its own).
 */
export function attachDecimationKeys(geometry) {
  const count = geometry?.attributes?.position?.count;
  if (!count) return;
  const keys = new Float32Array(count);
  for (let i = 0; i < count; i++) keys[i] = Math.random();
  geometry.setAttribute('aDecimateKey', new THREE.BufferAttribute(keys, 1));
}

/**
 * Enables/updates (or disables) the zone-isolation clip box on a culling material. Pass the
 * world→box-local matrix (inverse of the zone's world matrix on a unit cube) to isolate; pass
 * null to turn isolation off. No-op for materials without installCullShader.
 */
export function setMaterialClipBox(material, matrixInverse) {
  if (!material || !material.userData.cullShaderInstalled) return;

  const enabled = matrixInverse ? 1 : 0;
  material.userData.clipEnabled = enabled;
  if (matrixInverse) material.userData.clipMatrix.copy(matrixInverse);

  const shader = material.userData.shader;
  if (shader?.uniforms?.uClipEnabled) {
    shader.uniforms.uClipEnabled.value = enabled;
    if (matrixInverse) shader.uniforms.uClipMatrix.value.copy(matrixInverse);
  } else {
    // No compile yet — flag one so onBeforeCompile seeds the uniforms from userData.
    material.needsUpdate = true;
  }
}

/**
 * Enables/updates (or disables) the axis-aligned world-space slice box (cut-away tool) on a culling
 * material. Pass a box `{ min:{x,y,z}, max:{x,y,z} }` in WORLD coordinates to slice; pass null to
 * turn slicing off. Independent of the zone clip box. No-op for materials without installCullShader.
 */
export function setMaterialSliceBox(material, box) {
  if (!material || !material.userData.cullShaderInstalled) return;

  const enabled = box ? 1 : 0;
  material.userData.sliceEnabled = enabled;
  if (box) {
    material.userData.sliceMin.set(box.min.x, box.min.y, box.min.z);
    material.userData.sliceMax.set(box.max.x, box.max.y, box.max.z);
  }

  const shader = material.userData.shader;
  if (shader?.uniforms?.uSliceEnabled) {
    shader.uniforms.uSliceEnabled.value = enabled;
    if (box) {
      shader.uniforms.uSliceMin.value.set(box.min.x, box.min.y, box.min.z);
      shader.uniforms.uSliceMax.value.set(box.max.x, box.max.y, box.max.z);
    }
  } else {
    // No compile yet — flag one so onBeforeCompile seeds the uniforms from userData.
    material.needsUpdate = true;
  }
}

/**
 * Precomputes, for every vertex, the distance to its nearest cull-centre and stores it as the
 * `aMinDistance` BufferAttribute. The shader uses this for an O(1) per-vertex compare each frame;
 * without it the slider would have to iterate every helper per vertex per frame (millions × tens
 * of helpers — sluggish at best).
 *
 * Yields every TRIM_CHUNK points so the UI thread can still paint while we crunch ~N×M ops.
 */
export async function attachCullDistances(geometry, centers) {
  const positions = geometry?.attributes?.position?.array;
  if (!positions || !centers?.length) return;

  const count = positions.length / 3;
  const dists = new Float32Array(count);

  for (let start = 0; start < count; start += TRIM_CHUNK) {
    const end = Math.min(start + TRIM_CHUNK, count);
    for (let i = start; i < end; i++) {
      const px = positions[i * 3];
      const py = positions[i * 3 + 1];
      const pz = positions[i * 3 + 2];

      let minSq = Infinity;
      for (let j = 0; j < centers.length; j++) {
        const c = centers[j];
        const dx = px - c.x;
        const dy = py - c.y;
        const dz = pz - c.z;
        const d = dx * dx + dy * dy + dz * dz;
        if (d < minSq) minSq = d;
      }
      dists[i] = Math.sqrt(minSq);
    }
    await new Promise((resolve) => setTimeout(resolve, 0));
  }

  geometry.setAttribute('aMinDistance', new THREE.BufferAttribute(dists, 1));
}

/**
 * Builds a cumulative histogram of a geometry's `aMinDistance` values so the trim UI can report how
 * many points survive a given radius without rescanning millions of values on every slider tick.
 * Returns null when the geometry has no cull distances (trim unavailable). `cumulative[b]` holds the
 * number of points whose distance falls at or below that bucket's upper edge, so a single indexed
 * read answers "how many points are visible at radius r".
 */
export function buildTrimDistribution(geometry, buckets = 1024) {
  const dist = geometry?.attributes?.aMinDistance?.array;
  if (!dist?.length) return null;

  let max = 0;
  for (let i = 0; i < dist.length; i++) {
    if (dist[i] > max) max = dist[i];
  }
  if (max <= 0) return null;

  const counts = new Uint32Array(buckets);
  const scale = buckets / max;
  for (let i = 0; i < dist.length; i++) {
    let b = (dist[i] * scale) | 0;
    if (b >= buckets) b = buckets - 1;
    counts[b]++;
  }
  for (let i = 1; i < buckets; i++) counts[i] += counts[i - 1];

  return { cumulative: counts, max, total: dist.length };
}

/**
 * Number of points still visible at a trim radius (those with aMinDistance <= radius). radius <= 0
 * means trim is off, so everything is visible — matching the shader, which only culls when
 * uTrimRadius > 0. Returns null when no distribution exists (caller should treat all as visible).
 */
export function visibleAtRadius(distribution, radius) {
  if (!distribution) return null;
  const { cumulative, max, total } = distribution;
  if (radius <= 0 || radius >= max) return total;
  const buckets = cumulative.length;
  let b = ((radius / max) * buckets) | 0;
  if (b >= buckets) b = buckets - 1;
  return cumulative[b];
}

/**
 * Builds a teal polyline connecting consecutive centres (the "walking path" through the camera
 * positions). Rendered with depthTest:false so it stays visible inside dense point clouds; the
 * caller controls visibility via `line.visible`.
 *
 * The caller is responsible for ordering the centres in capture sequence — typically by sorting
 * the source camera helpers by imageName before extracting positions.
 */
export function createCameraPath(centers, { color = 0x64ffda, opacity = 0.9, linewidth = 2.5 } = {}) {
  if (!centers || centers.length < 2) return null;

  const positions = [];
  for (let i = 0; i < centers.length; i++) {
    positions.push(centers[i].x, centers[i].y, centers[i].z);
  }

  // Fat line (Line2) instead of THREE.Line: WebGL ignores LineBasicMaterial.linewidth, so a plain
  // line is always 1px. LineMaterial renders a real pixel-width line and supports dashes, which we
  // animate (dashOffset) on the selected scan to show the capture/walk direction.
  const geometry = new LineGeometry();
  geometry.setPositions(positions);

  const material = new LineMaterial({
    color: new THREE.Color(color),   // accepts a hex number or a '#rrggbb' string
    linewidth,          // pixels (worldUnits:false)
    worldUnits: false,
    transparent: true,
    opacity,
    depthTest: false,
    dashed: true,
    dashSize: 0.6,      // world units along the path
    gapSize: 0.4,
  });
  // LineMaterial needs the viewport size to size the pixel width; the engine keeps this in sync on
  // resize, but seed it now so the line is correct before the first resize event.
  material.resolution.set(
    typeof window !== 'undefined' ? window.innerWidth : 1,
    typeof window !== 'undefined' ? window.innerHeight : 1,
  );

  const line = new Line2(geometry, material);
  line.computeLineDistances();  // required for the dash pattern
  line.renderOrder = 999;       // drawn after the points pass so depthTest:false reads as expected
  return line;
}

/**
 * Common 360-capture cubemap face suffix detection. Cameras stored per face (6 helpers at the
 * same position) collapse to one capture point for trim-distance and path drawing.
 */
const CUBE_FACE_RE = /^(.+?)[_-](front|back|left|right|up|down|top|bottom|px|nx|py|ny|pz|nz)(\.[^.]+)?$/i;

/**
 * Thin an ordered list of capture centres down to "keyframes" for the *drawn* walking path, so the
 * line stays a clean, readable trail instead of a dense node-per-capture polyline. Decimation is by
 * cumulative arc-length along the capture sequence (centres must already be in capture order), which
 * preserves the path shape and adapts to walking speed; the first and last capture are always kept.
 *
 * This is purely cosmetic for the line — peg-drop snapping, playback and cull distances still run on
 * the full centre set, so dropping the street-view man always selects the closest actual camera.
 *
 * `everyN` sets the target spacing as a multiple of the median segment length (default 3 ≈ keep
 * roughly one in three captures on evenly-spaced footage).
 */
export function decimatePathCenters(centers, { everyN = 3 } = {}) {
  if (!centers || centers.length <= 2) return centers ? [...centers] : [];

  const seg = (a, b) => Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
  const gaps = [];
  for (let i = 1; i < centers.length; i++) gaps.push(seg(centers[i - 1], centers[i]));

  const sorted = [...gaps].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)] || 0;
  const step = median * everyN;
  if (!(step > 0)) return [...centers];

  const out = [centers[0]];
  let acc = 0;
  for (let i = 1; i < centers.length - 1; i++) {
    acc += gaps[i - 1];
    if (acc >= step) { out.push(centers[i]); acc = 0; }
  }
  out.push(centers[centers.length - 1]);
  return out;
}

/**
 * 360 captures produce 6 helpers per capture point (one per cubemap face: front/back/left/right/
 * up/down, or the px/nx/... variants). Their positions are identical so they're redundant for
 * trim distances and would draw 6 stacked nodes in the camera path. Collapse to one helper per
 * capture, preferring the front face (or pz) when present.
 */
export function dedupeCubemapHelpers(helpers) {
  const byBase = new Map();

  for (const h of helpers ?? []) {
    const name = h?.imageName ?? '';
    const match = name.match(CUBE_FACE_RE);
    if (!match) {
      byBase.set(name || `__no-name-${byBase.size}`, h);
      continue;
    }

    const base = match[1].toLowerCase();
    const face = match[2].toLowerCase();
    const existing = byBase.get(base);
    const isPreferredFace = face === 'front' || face === 'pz';
    if (!existing || isPreferredFace) byBase.set(base, h);
  }

  return [...byBase.values()];
}

/**
 * Helper → {x,y,z} position extraction, filtering anything with a missing/non-finite component.
 * Used after `dedupeCubemapHelpers` (+ a sort by imageName, when the caller wants the path to
 * trace capture sequence) to produce the centres array consumed by attachCullDistances /
 * createCameraPath.
 */
export function helperPositions(helpers) {
  return (helpers ?? [])
    .map(h => h?.transform?.position)
    .filter(p => p && Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z))
    .map(p => ({ x: p.x, y: p.y, z: p.z }));
}
